import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
    FolderUpdate,
    Folder,
    FolderResponse
} from '../core-data-model/Folder.model';
import { IFoldersApi } from '../core-data-model/FoldersApi.connectinterface';
import { FolderServer } from '../core-data-model/FoldersApi.serverinterface';
import { FolderTransformers } from '../core-services/FoldersApi.datatransform';
import { ApiService } from '../system-services/Api.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { FoldersApiService } from './folders/folders-api.service';

/**
 * v3 CRUD api endpoints for Folders
 *
 */
@Injectable({
    providedIn: 'root'
})
export class FoldersApi implements IFoldersApi {
    constructor(
        private api: ApiService,
        private userProfile: UserProfileSettingsService,
        private folderTransformers: FolderTransformers,
        private newFoldersApiService: FoldersApiService
    ) {}

    getFolder(
        folderId: string,
        companyId: string = this.userProfile.currentCompany
    ) {
        return this.api
            .get<FolderServer>(
                `/api/v3/companies/${companyId}/folders/${folderId}`
            )
            .then(([response]) =>
                this.folderTransformers.transformWithDetails(response)
            );
    }

    getAllFoldersForCompany(companyId: string): Promise<Folder[]> {
        return this.api
            .get<FolderServer[]>(`/api/v3/companies/${companyId}/folders/`)
            .then(([response]) =>
                this.folderTransformers.transformAll(response)
            );
    }

    updateFolder(
        folder: FolderUpdate,
        companyId: string = this.userProfile.currentCompany
    ): Promise<any> {
        if (folder.originalObject) {
            return this.api.patch(
                `/api/v3/companies/${companyId}/folders/${folder.originalObject.id}`,
                this.folderTransformers.transformToFolderServer(folder)
            );
        } else {
            // New folder
            return this.api
                .post(
                    `/api/v3/companies/${companyId}/folders`,
                    this.folderTransformers.transformToFolderServer(folder)
                )
                .then(([response]) =>
                    this.folderTransformers.transform(response)
                );
        }
    }

    /**
     *
     * @param folder
     * @param companyId
     * @returns A Promise.
     * @deprecated Use the new {@link FoldersApiService.deleteFolder} instead.
     */
    deleteFolder(
        folder: Folder,
        companyId: string = this.userProfile.currentCompany
    ): Promise<any> {
        return this.api
            .delete(`/api/v3/companies/${companyId}/folders/${folder.id}`)
            .then((...args) => args);
    }

    addAccessUsers(
        folderId: string,
        newUserIds: string[],
        companyId: string = this.userProfile.currentCompany
    ): Promise<any> {
        return Promise.all(
            newUserIds.map((userId) =>
                this.api
                    .post(
                        `/api/v3/companies/${companyId}/folders/${folderId}/directAccessUsers/${userId}`,
                        {}
                    )
                    .then((...args) => args)
            )
        );
    }

    deleteAccessUsers(
        folderId: string,
        oldUserIds: string[],
        companyId: string = this.userProfile.currentCompany
    ): Promise<any> {
        return Promise.all(
            oldUserIds.map((userId) =>
                this.api
                    .delete(
                        `/api/v3/companies/${companyId}/folders/${folderId}/directAccessUsers/${userId}`
                    )
                    .then((...args) => args)
            )
        );
    }

    /** **** Leaving these pass throughs for now so the change isn't breaking ****** */

    /**
     * @deprecated Use {@link FoldersApiService.getFolderAndChildren} this method is a passthrough.
     */
    getFolderAndChildren(folderId?: string): Observable<FolderResponse> {
        return this.newFoldersApiService.getFolderAndChildren(folderId);
    }

    /**
     * @deprecated Use {@link FoldersApiService.getFolderTree} this method is a passthrough.
     */
    getFolderTree(folderId?: string): Observable<FolderResponse> {
        return this.newFoldersApiService.getFolderTree(folderId);
    }

    /**
     * @deprecated Use {@link FoldersApiService.getFolderTreeForUser} this method is a passthrough.
     */
    getFolderTreeForUser(userId?: string): Observable<FolderResponse> {
        return this.newFoldersApiService.getFolderTreeForUser(userId);
    }
}
